import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import usePiwik from 'src/hooks/usePiwik'
import { getConfig } from 'src/features/config/ConfigSelector'
import { stripTags, truncateText } from 'src/utils'

/**
 * Styled Components
 */
const Container = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  borderRadius: '16px',
  marginTop: '8px',
  border: `1px solid ${theme.palette.divider}`
}))

const Card = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px',
  marginBottom: '8px',
  borderRadius: '8px'
}))

const ReportInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px'
}))

const ReportType = styled(Typography)(({ theme }) => ({
  color: theme.palette.relatedReports.type,
  fontSize: '14px'
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.primary,
  fontWeight: 600,
  lineHeight: 1.5,
  transition: 'color 0.2s ease'
}))

const Teaser = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  fontWeight: 400,
  lineHeight: 1.5
}))

/**
 * RelatedReports Component
 */
const RelatedReports = ({ articles }) => {
  const refs = useRef({})
  const { handleActionTracking } = usePiwik()
  const { t } = useTranslation()
  const config = useSelector(getConfig('external_sites'))
  const reportsUrl = config?.Reports?.url || 'https://firstwordreports.com'

  useEffect(() => {
    const observers = []

    if (articles?.length > 0) {
      articles.forEach(article => {
        const el = refs.current[article.id]
        if (!el) return

        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            handleActionTracking('Site/View/Answer/RelatedReports', {
              articleId: article.id
            })
            observer.disconnect()
          }
        })

        observer.observe(el)
        observers.push(observer)
      })
      return () => observers.forEach(observer => observer.disconnect())
    }
  }, [articles, handleActionTracking])

  const handleRelatedReportClick = (reportId, url) => {
    handleActionTracking('Site/Click/Answer/RelatedReports', {
      articleId: reportId
    })
    window.open(url, '_blank')
  }

  return (
    <Container data-testid="related-reports">
      <Typography
        variant="h1"
        textAlign="center"
        fontWeight="bold"
        fontSize="16px"
        mb="8px"
      >
        {t('relatedReports').toUpperCase()}
      </Typography>
      {articles?.map(article => {
        const id = article.id
        const title = article.title || 'Untitled'
        const url = `${reportsUrl}/story/${id}`
        const formattedBody = stripTags(article?.body || '')
        const teaser = truncateText(formattedBody)
        const type = article?.type

        return (
          <Card
            key={id}
            ref={el => (refs.current[id] = el)}
            onClick={() => handleRelatedReportClick(id, url)}
            sx={{ cursor: 'pointer' }}
          >
            <ReportInfo>
              <Title>{title}</Title>
              <ReportType>
                <Chip size="small" label={type} />
              </ReportType>
            </ReportInfo>
            {teaser && <Teaser>{teaser}</Teaser>}
          </Card>
        )
      })}
    </Container>
  )
}

RelatedReports.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      type: PropTypes.string,
      body: PropTypes.string,
      extra_fields: PropTypes.object
    })
  )
}

export default RelatedReports
